<template>
	<div>
		<div class="db-hero">
			<div class="db-hero-top">
				<div>
					<h1>{{ title }}</h1>
					<p v-if="subtitle">{{ subtitle }}</p>
				</div>
			</div>
			<div class="db-stat-row">
				<div v-for="(stat, index) in heroStats" :key="stat.label" class="db-stat" :class="{ first: index === 0 }">
					<div class="db-stat-label">{{ stat.label }}</div>
					<div class="db-stat-value">{{ stat.value }}</div>
					<div v-if="stat.sub" class="db-stat-sub">{{ stat.sub }}</div>
				</div>
			</div>
		</div>

		<div class="db-grid">
			<div class="db-card">
				<h3 v-if="snapshotTitle">{{ snapshotTitle }}</h3>
				<div class="db-mini-grid">
					<div v-for="item in snapshotStats" :key="item.label" class="db-mini" @click="$emit('stat-click')">
						<div class="db-icon">
							<b-img :src="iconSrc(item.icon)" style="width: 22px; height: 22px" />
						</div>
						<div>
							<div class="db-mini-label">{{ item.label }}</div>
							<div class="db-mini-value">{{ item.value }}</div>
						</div>
					</div>
				</div>
				<div v-if="$slots.chart" class="db-chart-slot">
					<slot name="chart" />
				</div>
			</div>
			<div class="db-card">
				<h3>Needs attention</h3>
				<div v-if="alerts.length">
					<div v-for="(alert, index) in alerts" :key="index" class="db-alert-row">
						<span class="db-chip" :class="`db-chip-${alert.variant}`">{{ alert.chip }}</span>
						{{ alert.text }}
					</div>
				</div>
				<div v-else class="db-alert-empty">Everything looks on track for this period.</div>
			</div>
		</div>
	</div>
</template>

<script>
import { BImg } from "bootstrap-vue";

export default {
	components: { BImg },
	props: {
		title: { type: String, required: true },
		subtitle: { type: String, default: "" },
		snapshotTitle: { type: String, default: "Snapshot" },
		heroStats: { type: Array, default: () => [] },
		snapshotStats: { type: Array, default: () => [] },
		alerts: { type: Array, default: () => [] },
	},
	methods: {
		iconSrc(icon) {
			return typeof icon === "string" ? icon : icon && icon.default;
		},
	},
};
</script>

<style lang="scss" scoped>
.db-hero {
	background: linear-gradient(135deg, #7367f0 0%, #9c93f5 55%, #8fa1f7 100%);
	border-radius: 20px;
	padding: 24px 26px 20px;
	color: #fff;
	margin-bottom: 16px;
	position: relative;
	overflow: hidden;

	&::after {
		content: "";
		position: absolute;
		right: -60px;
		top: -60px;
		width: 220px;
		height: 220px;
		border-radius: 50%;
		background: rgba(255, 255, 255, 0.08);
	}
}
.db-hero-top h1 {
	margin: 0;
	font-size: 20px;
	font-weight: 700;
	color: #fff !important;
}
.db-hero-top p {
	margin: 4px 0 0;
	font-size: 13px;
	opacity: 0.85;
	color: #fff !important;
}
.db-stat-row {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
	gap: 18px;
	margin-top: 20px;
	position: relative;
}
.db-stat {
	border-left: 1px solid rgba(255, 255, 255, 0.25);
	padding-left: 14px;

	&.first {
		border-left: none;
		padding-left: 0;
	}
}
.db-stat-label {
	font-size: 11.5px;
	opacity: 0.85;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.db-stat-value {
	font-size: 24px;
	font-weight: 700;
	margin-top: 4px;
}
.db-stat-sub {
	font-size: 11.5px;
	opacity: 0.85;
	margin-top: 2px;
}

.db-grid {
	display: grid;
	grid-template-columns: 1.3fr 1fr;
	gap: 16px;
	margin-bottom: 16px;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
}
.db-card {
	background: #fff;
	border-radius: 16px;
	padding: 20px;
	box-shadow: 0 2px 14px rgba(94, 84, 178, 0.06);

	h3 {
		margin: 0 0 14px;
		font-size: 15px;
	}
}
.db-mini-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 12px;
}
.db-mini {
	background: #f8f7fc;
	border-radius: 12px;
	padding: 14px;
	display: flex;
	align-items: center;
	gap: 12px;
	cursor: pointer;
	transition: background 0.15s ease;

	&:hover {
		background: #ede9fe;
	}
}
.db-icon {
	width: 38px;
	height: 38px;
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ede9fe;
	flex-shrink: 0;
}
.db-mini-label {
	font-size: 11.5px;
	color: #6b6685;
}
.db-mini-value {
	font-size: 17px;
	font-weight: 700;
}
.db-chart-slot {
	margin-top: 18px;
}

.db-alert-row {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 0;
	border-bottom: 1px solid #e8e6f3;
	font-size: 13px;

	&:last-child {
		border-bottom: none;
	}
}
.db-alert-empty {
	font-size: 13px;
	color: #6b6685;
	padding: 10px 0;
}
.db-chip {
	padding: 3px 9px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 700;
	white-space: nowrap;
}
.db-chip-critical {
	background: #fdeaea;
	color: #ea5455;
}
.db-chip-warn {
	background: #fff2e3;
	color: #b5680b;
}
.db-chip-good {
	background: #e7f9ef;
	color: #1a8a4e;
}
</style>
